// src/analytics.js
// Lightweight GA4 wiring for a React SPA (Nifty Finance)
// - Injects gtag early (no window.load delay)
// - Manual SPA page_view tracking (trackPage + history patch)
// - Auto-captures [data-ev] clicks/changes
// - Dedupe guards for boot + identify events

export const MEASUREMENT_ID =
  process.env.REACT_APP_GTAG_MEASUREMENT_ID || ''; // ✅ Correct key

let booted = false;
let _identifiedUserId = null;
let lastIntakeSubmitAt = 0;

// ------------------------------
// Core: dataLayer/gtag bootstrap
// ------------------------------
function ensureDL() {
  // Avoid stomping existing DL/gtag if snippet exists
  // eslint-disable-next-line no-multi-assign
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line func-names
  window.gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments);
    };
}
ensureDL();

// --- Inbound attribution (UTMs + referrer) ---
export const ATTR_KEYS = {
  first: 'nf_attrib_first',
  last:  'nf_attrib_last',
  ses:   'nf_attrib_session',
};

const SOCIAL_DOMAIN_MAP = {
  // X / Twitter
  'x.com': 'x', 'twitter.com': 'x', 't.co': 'x',
  // Meta
  'facebook.com': 'facebook', 'm.facebook.com': 'facebook', 'messenger.com': 'facebook',
  'instagram.com': 'instagram', 'l.instagram.com': 'instagram', 'threads.net': 'instagram',
  // LinkedIn
  'linkedin.com': 'linkedin', 'lnkd.in': 'linkedin',
  // Others
  'reddit.com': 'reddit',
  'ycombinator.com': 'hackernews',
  'medium.com': 'medium',
  'tiktok.com': 'tiktok',
  'youtube.com': 'youtube', 'youtu.be': 'youtube',
  'discord.com': 'discord', 'discord.gg': 'discord',
  'whatsapp.com': 'whatsapp',
  't.me': 'telegram', 'telegram.me': 'telegram',
  'producthunt.com': 'producthunt',
  'linktr.ee': 'linktree',
  'mailchi.mp': 'mailchimp',
  'substack.com': 'substack',
  'beehiiv.com': 'beehiiv',
  'notion.site': 'notion',
  'bit.ly': 'shortener', 'tinyurl.com': 'shortener',
};

const SEARCH_DOMAIN_MEDIUM = {
  'google.com':      { source: 'google',     medium: 'organic' },
  'bing.com':        { source: 'bing',       medium: 'organic' },
  'duckduckgo.com':  { source: 'duckduckgo', medium: 'organic' },
  'yahoo.com':       { source: 'yahoo',      medium: 'organic' },
  'ecosia.org':      { source: 'ecosia',     medium: 'organic' },
  'startpage.com':   { source: 'startpage',  medium: 'organic' },
  'brave.com':       { source: 'brave',      medium: 'organic' }, // search.brave.com → brave.com base
  'baidu.com':       { source: 'baidu',      medium: 'organic' },
};

const EMAIL_DOMAIN_MEDIUM = {
  'mail.google.com':   { source: 'gmail',   medium: 'email' },
  'outlook.live.com':  { source: 'outlook', medium: 'email' },
  'outlook.office.com':{ source: 'outlook', medium: 'email' },
  'mail.yahoo.com':    { source: 'yahoo',   medium: 'email' },
  'proton.me':         { source: 'proton',  medium: 'email' },
  'mail.aol.com':      { source: 'aol',     medium: 'email' },
};

function baseDomain(host) {
  try {
    const parts = (host || '').toLowerCase().split('.').filter(Boolean);
    return parts.length <= 2 ? parts.join('.') : parts.slice(-2).join('.');
  } catch { return ''; }
}
function safeJSON(x) { try { return JSON.parse(x); } catch { return null; } }

function readUTMs() {
  const qs = new URLSearchParams(window.location.search || '');
  const get = (k) => (qs.get(k) || '').trim();
  const obj = {
    source:   get('utm_source'),
    medium:   get('utm_medium'),
    campaign: get('utm_campaign'),
    content:  get('utm_content'),
    term:     get('utm_term'),
    gclid:    get('gclid') || get('gbraid') || get('wbraid') || '',
    fbclid:   get('fbclid') || '',
    twclid:   get('twclid') || '',
  };
  Object.keys(obj).forEach(k => { if (!obj[k]) delete obj[k]; });
  return obj;
}

function referrerAttribution() {
  const ref = document.referrer || '';
  if (!ref) return { referrer: '', source: '(direct)', medium: '(none)' };

  try {
    const u = new URL(ref);
    const host = baseDomain(u.hostname);
    const myHost = baseDomain(window.location.hostname || '');

    // Internal navs shouldn't overwrite attribution
    if (host === myHost) return { referrer: ref, source: '(internal)', medium: '(none)' };

    // Email/webmail portals
    if (EMAIL_DOMAIN_MEDIUM[host]) {
      const { source, medium } = EMAIL_DOMAIN_MEDIUM[host];
      return { referrer: ref, source, medium };
    }

    // Organic search
    if (SEARCH_DOMAIN_MEDIUM[host]) {
      const { source, medium } = SEARCH_DOMAIN_MEDIUM[host];
      return { referrer: ref, source, medium };
    }

    // Social sites (treat as social medium)
    if (SOCIAL_DOMAIN_MAP[host]) {
      return { referrer: ref, source: SOCIAL_DOMAIN_MAP[host], medium: 'social' };
    }

    // Generic referral
    return { referrer: ref, source: host || '(referrer)', medium: 'referral' };
  } catch {
    return { referrer: ref, source: '(referrer)', medium: 'referral' };
  }
}

// If click IDs are present, infer paid channels when UTMs didn't specify
function enrichWithClickIds(touch) {
  const t = { ...touch };
  const qs = new URLSearchParams(window.location.search || '');

  if ((qs.has('gclid') || qs.has('gbraid') || qs.has('wbraid'))) {
    if (!t.source || t.source === '(direct)') t.source = 'google';
    if (!t.medium || t.medium === '(none)' || t.medium === 'referral') t.medium = 'cpc';
    t.gclid = qs.get('gclid') || qs.get('gbraid') || qs.get('wbraid') || '';
  }
  if (qs.has('fbclid')) {
    if (!t.source || t.source === '(direct)') t.source = 'facebook';
    if (!t.medium || t.medium === '(none)' || t.medium === 'referral') t.medium = 'paid_social';
    t.fbclid = qs.get('fbclid');
  }
  if (qs.has('twclid')) {
    if (!t.source || t.source === '(direct)') t.source = 'x';
    if (!t.medium || t.medium === '(none)' || t.medium === 'referral') t.medium = 'paid_social';
    t.twclid = qs.get('twclid');
  }
  return t;
}

function currentTouch() {
  const utm = readUTMs();
  const base = (utm.source || utm.medium || utm.campaign) ? { ...utm, referrer: document.referrer || '' }
                                                          : referrerAttribution();
  return enrichWithClickIds(base);
}

// One-time bootstrap: store first/last/session, set user props, fire "landing"
export function bootstrapAttribution() {
  if (!MEASUREMENT_ID) return;

  const touch = currentTouch();
  const now = new Date().toISOString();

  try {
    const first = safeJSON(localStorage.getItem(ATTR_KEYS.first));
    if (!first) localStorage.setItem(ATTR_KEYS.first, JSON.stringify({ ...touch, ts: now }));
    localStorage.setItem(ATTR_KEYS.last, JSON.stringify({ ...touch, ts: now }));
    sessionStorage.setItem(ATTR_KEYS.ses, JSON.stringify({ ...touch, ts: now }));
  } catch {}

  const ft = safeJSON(localStorage.getItem(ATTR_KEYS.first)) || {};
  const lt = safeJSON(localStorage.getItem(ATTR_KEYS.last))  || touch;

  setUserProps({
    ft_source:   ft.source || '(direct)',
    ft_medium:   ft.medium || '(none)',
    ft_campaign: ft.campaign || '(none)',
    lt_source:   lt.source || '(direct)',
    lt_medium:   lt.medium || '(none)',
    lt_campaign: lt.campaign || '(none)',
  });

  if (!sessionStorage.getItem('nf_landed')) {
    sessionStorage.setItem('nf_landed', '1');
    track('landing', {
      ...touch,
      page_location: window.location?.href || '',
      page_path: ((window.location?.pathname || '') + (window.location?.search || '')),
    });
  }
}

// Optional: read attribution later (e.g., enrich home_view)
export function getAttribution() {
  const ses = safeJSON(sessionStorage.getItem(ATTR_KEYS.ses));
  const ft  = safeJSON(localStorage.getItem(ATTR_KEYS.first));
  const lt  = safeJSON(localStorage.getItem(ATTR_KEYS.last));
  return { session: ses, first: ft, last: lt };
}


// Public thin wrapper (keeps our own ensureDL safety)
function gtag() {
  ensureDL();
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export function initAnalytics({ sendPageView = false, cookieDomain = 'niftyfinance.com' } = {}) {
  if (booted || !MEASUREMENT_ID) return;
  booted = true;

  // If a GA tag already exists (e.g., inserted via index.html), don't inject again
  const src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  if (!document.querySelector(`script[src="${src}"]`)) {
    const s = document.createElement('script');
    s.async = true;
    s.src = src;
    document.head.appendChild(s);
  }

  // Configure immediately when gtag is ready; our stub queues until tag loads
  gtag('js', new Date());
  gtag('config', MEASUREMENT_ID, {
    send_page_view: sendPageView, // SPA: false here; we send page_view manually
    cookie_domain: cookieDomain,
  });
}

// ---------------
// Event helpers
// ---------------
export function track(name, params = {}) {
  if (!MEASUREMENT_ID) return;
  gtag('event', name, { ...params });
}

export function setUserId(uid) {
  if (!uid || !MEASUREMENT_ID) return;
  gtag('config', MEASUREMENT_ID, { user_id: uid });
}

export function setUserProps(props = {}) {
  if (!MEASUREMENT_ID) return;
  gtag('set', 'user_properties', props);
}

// ----------------------------
// SPA page_view instrumentation
// ----------------------------
export function trackPage(path) {
  if (!MEASUREMENT_ID) return;
  const href =
    (typeof window !== 'undefined' && window.location && window.location.href) || '';
  const p =
    path ??
    (((window.location && window.location.pathname) || '') +
      ((window.location && window.location.search) || ''));
  track('page_view', { page_location: href, page_path: p });
}

// ---- SPA route change bridge (for intake → dashboard completion) ----
(function patchHistoryOnce() {
  if (patchHistoryOnce._did) return;
  patchHistoryOnce._did = true;

  const push = window.history.pushState.bind(window.history);
  const rep = window.history.replaceState.bind(window.history);
  const fire = () => window.dispatchEvent(new Event('nf:locationchange'));

  // eslint-disable-next-line prefer-rest-params
  window.history.pushState = function () {
    const r = push(...arguments);
    fire();
    return r;
  };
  // eslint-disable-next-line prefer-rest-params
  window.history.replaceState = function () {
    const r = rep(...arguments);
    fire();
    return r;
  };
  window.addEventListener('popstate', fire);
})();

// -----------------
// Public entrypoint
// -----------------
export function wirePage(kind, meta = {}) {
  // Send a page_view for every mount (manual SPA tracking)
  trackPage();
  enableAutoCapture();

  if (kind === 'home') return wireHome();
  if (kind === 'intake') return wireIntake();
  if (kind === 'dashboard') return wireDashboard();

  // Generic landing/PSEO pages
  if (kind === 'lander') {
    const slug = meta.slug || meta.id || 'unknown';
    track('lander_view', { slug });
  }
}

// ------------------
// data-ev auto-capture
// ------------------
function enableAutoCapture() {
  if (enableAutoCapture._on) return;
  enableAutoCapture._on = true;

  document.addEventListener(
    'click',
    (e) => {
      const el = e.target.closest?.('[data-ev]');
      if (!el) return;
      const name = el.getAttribute('data-ev');
      if (!name) return;

      let params = {};
      const json = el.getAttribute('data-params');
      if (json) {
        try {
          params = JSON.parse(json);
        } catch {
          /* noop */
        }
      } else {
        for (const [k, v] of Object.entries(el.dataset)) {
          if (k === 'ev' || k === 'params') continue;
          if (k.startsWith('ev')) params[decap(k.slice(2))] = coerce(v);
        }
      }

      if (name === 'intake_submit') lastIntakeSubmitAt = Date.now();
      track(name, params);
    },
    true
  );

  document.addEventListener(
    'change',
    (e) => {
      const el = e.target.closest?.('[data-ev]');
      if (!el) return;
      const name = el.getAttribute('data-ev');
      if (!name) return;

      let params = {};
      const json = el.getAttribute('data-params');
      if (json) {
        try {
          params = JSON.parse(json);
        } catch {
          /* noop */
        }
      } else {
        for (const [k, v] of Object.entries(el.dataset)) {
          if (k === 'ev' || k === 'params') continue;
          if (k.startsWith('ev')) params[decap(k.slice(2))] = coerce(v);
        }
        if (e.target instanceof window.HTMLInputElement && 'checked' in e.target) {
          params.enabled = !!e.target.checked;
        }
      }
      track(name, params);
    },
    true
  );
}

// ---------------------
// Page-specific wiring
// ---------------------
function wireHome() {
  const k = 'nf_home_seen';
  const returning = window.localStorage?.getItem(k) ? 'true' : 'false';
  window.localStorage?.setItem(k, '1');

  let src = '(direct)', med = '(none)', camp = '(none)';
  try {
    const { session = {} } = getAttribution() || {};
    src = session.source || src;
    med = session.medium || med;
    camp = session.campaign || camp;
  } catch {}

  track('home_view', { is_returning_session: returning, src, med, camp });
}


function wireIntake() {
  const qs = new window.URLSearchParams(window.location?.search || '');
  const mode = qs.get('edit') === 'true' ? 'edit' : 'create';
  track('intake_view', { mode });
  if (mode === 'edit') track('intake_resume_draft');

  window.addEventListener('nf:intake_saved', () => {
    track('intake_save_draft_success');
  });

  window.addEventListener('nf:locationchange', () => {
    const path = (window.location?.pathname || '').toLowerCase();
    if (lastIntakeSubmitAt && Date.now() - lastIntakeSubmitAt <= 15_000 && path.includes('/mydashboard')) {
      track('intake_complete');
      setUserProps({ has_completed_intake: 'true' });
      lastIntakeSubmitAt = 0;
    }
  });
}

function wireDashboard() {
  track('dashboard_view');
  window.addEventListener('nf:week_saved', (e) => {
    const weekId = e?.detail?.weekId;
    track('week_save', { week_id: weekId });
  });
}

// ----------------------
// Auth identify + dedupe
// ----------------------
export function identifyAuth(user) {
  if (!user) return;
  if (_identifiedUserId === user.uid) return; // Session guard
  _identifiedUserId = user.uid;

  setUserId(user.uid);

  const created = new Date(user.metadata?.creationTime || 0).getTime();
  const last = new Date(user.metadata?.lastSignInTime || 0).getTime();
  const first = Math.abs(last - created) < 15_000;

  // Emit sign_up only once per browser/user (avoid noisy re-installs)
  const signupKey = `nf_signup_emitted_${user.uid}`;
  if (first) {
    if (!window.localStorage?.getItem(signupKey)) {
      track('sign_up', { method: user.providerData?.[0]?.providerId || 'firebase' });
      window.localStorage?.setItem(signupKey, '1');
    }
    setUserProps({ is_new_user: 'true', signup_date: isoDate(user.metadata?.creationTime) });
  } else {
    track('login', { method: user.providerData?.[0]?.providerId || 'firebase' });
    setUserProps({ is_new_user: 'false' });
    window.localStorage?.setItem('nf_has_logged_in_before', '1');
  }

  track('retention_ping', {
    days_since_signup: dayDiff(new Date(), new Date(user.metadata?.creationTime || Date.now())),
  });
}

// ---------
// Utilities
// ---------
function isoDate(x) {
  const d = new Date(x || Date.now());
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
}
function dayDiff(a, b) {
  return Math.floor(Math.abs(a - b) / (1000 * 60 * 60 * 24));
}
function decap(s) {
  return s ? s[0].toLowerCase() + s.slice(1) : s;
}
function coerce(v) {
  if (v === 'true') return true;
  if (v === 'false') return false;
  if (!Number.isNaN(Number(v)) && String(v).trim() !== '') return Number(v);
  return v;
}

